import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { battlesApi } from '../../api';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbButton from '../controls/HrnbButton';
import HrnbSelect from '../controls/HrnbSelect';
import { getFormatIcon } from '../../utils/battleFormats';

const STATUS_LABELS = {
    PLANNED: 'Запланирован',
    ACTIVE: 'Активен',
    FINISHED: 'Завершён',
    CANCELLED: 'Отменён',
};

const STATUS_COLORS = {
    PLANNED: '#17a2b8',
    ACTIVE: '#28a745',
    FINISHED: '#6c757d',
    CANCELLED: '#dc3545',
};

export default function ActiveBattlesWidget() {
    const navigate = useNavigate();
    const [battles, setBattles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterFormat, setFilterFormat] = useState('');

    const FORMAT_OPTIONS = [
        { value: '', label: 'Все форматы' },
        { value: 'AUDIO', label: '🎵 Аудио' },
        { value: 'TEXT', label: '📝 Текст' },
    ];

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                const params = { size: 5, status: 'ACTIVE' };
                if (filterFormat) params.format = filterFormat;
                const data = await battlesApi.list(params);
                const list = data?.content || data?.data || data || [];
                if (mounted) setBattles(Array.isArray(list) ? list.slice(0, 5) : []);
            } catch (err) {
                console.error('[ActiveBattlesWidget]', err.message);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetch();
        return () => { mounted = false; };
    }, [filterFormat]);

    const handleNavigate = () => navigate('/battles');

    if (loading) {
        return (
            <div className="main-widget">
                <h3 className="main-widget__title">⚔️ Активные баттлы</h3>
                <div className="main-widget__loading">
                    <HrnbLoader size={32} text="Загрузка..." />
                </div>
            </div>
        );
    }

    const handleFormatChange = (value) => {
        setFilterFormat(value);
    };

    if (battles.length === 0) {
        return (
            <div className="main-widget">
                <h3 className="main-widget__title">⚔️ Активные баттлы</h3>
                <p className="main-widget__empty">Сейчас нет открытых баттлов</p>
                <HrnbButton variant="outline" size="sm" onClick={handleNavigate}>
                    ⚔️ Все баттлы
                </HrnbButton>
            </div>
        );
    }

    return (
        <div className="main-widget">
            <h3 className="main-widget__title">⚔️ Активные баттлы</h3>
            <div style={{ marginBottom: 12 }}>
                <HrnbSelect
                    value={filterFormat}
                    onChange={handleFormatChange}
                    options={FORMAT_OPTIONS}
                    placeholder="Фильтр по формату"
                    style={{ maxWidth: 180, fontSize: '0.9rem' }}
                />
            </div>
            <div className="main-widget__list">
                {battles.map((battle) => (
                    <BattleItem key={battle.id} battle={battle} onClick={() => navigate(`/battles/${battle.id}`)} />
                ))}
            </div>
            <HrnbButton variant="outline" size="sm" onClick={handleNavigate}>
                ⚔️ Все баттлы
            </HrnbButton>
        </div>
    );
}

function BattleItem({ battle, onClick }) {
    const statusLabel = STATUS_LABELS[battle.status] || battle.status;
    const statusColor = STATUS_COLORS[battle.status] || '#6c757d';
    const formatIcon = getFormatIcon(battle.format);

    return (
        <button className="main-widget__item main-widget__item--battle" onClick={onClick} type="button">
            <div className="main-widget__item-body">
                <div className="main-widget__item-header">
                    <span className="main-widget__item-text">{battle.title}</span>
                    <span className="main-widget__item-status" style={{ backgroundColor: statusColor }}>
                        {statusLabel}
                    </span>
                </div>
                <div className="main-widget__item-format" style={{ fontSize: '0.8rem', marginBottom: 6 }}>
                    {formatIcon} {battle.format || 'Не указан'}
                </div>
                {battle.description && (
                    <p className="main-widget__item-desc">
                        {battle.description.length > 80
                            ? `${battle.description.substring(0, 80)}...`
                            : battle.description}
                    </p>
                )}
                <div className="main-widget__item-meta">
                    {battle.startTime && (
                        <span>Начало: {formatDate(battle.startTime)}</span>
                    )}
                    {battle.maxParticipants && (
                        <span>Участники: {battle.participantCount || 0} / {battle.maxParticipants}</span>
                    )}
                </div>
            </div>
        </button>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
